import { useEffect, useMemo, useState } from 'react';
import { setExtension } from '../lib/api';
import { minutes, shortDate, today } from '../lib/dates';
import { passWord } from '../lib/kinds';
import { clock } from '../lib/reading';
import { supabase } from '../lib/supabase';
import type { Assignment, Pass, Progress } from '../lib/types';
import { useClassroom } from '../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// Results for one assignment, for grading (on the teacher's view of the
// assignment): every student's status, on time or late (against their own due
// date, if they have an extension), every attempt with its date and score,
// time, lookups or video watched, words saved, visits. Extend a student's due
// date right here, and download it all as a CSV for a gradebook.
// ─────────────────────────────────────────────────────────────────────────────

type Row = {
  id: string; name: string; username: string;
  p: Progress | undefined; passes: Pass[]; due: string | null; extended: boolean;
  status: 'done' | 'partial' | 'none'; timing: 'on time' | 'late' | 'missing' | 'not due yet' | '';
  lateDays: number; best: number | null; avg: number | null; words: number; pct: number;
};

const localDay = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${a}T12:00:00`) - Date.parse(`${b}T12:00:00`)) / 86_400_000);

export default function AssignmentResults({ a }: { a: Assignment }) {
  const { students, progressFor, passesFor, dueFor, reloadExtensions, refresh } = useClassroom();
  const [words, setWords] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useState<'name' | 'status'>('name');
  const t = today();

  // Words students saved from this assignment.
  useEffect(() => {
    void supabase.from('student_cards').select('student_id').eq('assignment_id', a.id).then(({ data }) => {
      const n: Record<string, number> = {};
      for (const r of (data ?? []) as { student_id: string }[]) n[r.student_id] = (n[r.student_id] ?? 0) + 1;
      setWords(n);
    });
  }, [a.id]);

  const rows: Row[] = useMemo(() => students.map(s => {
    const p = progressFor(a.id, s.user_id), passes = passesFor(a.id, s.user_id);
    const due = dueFor(a, s.user_id);
    const done = !!p?.completed_at, n = p?.completions ?? 0;
    const status: Row['status'] = done ? 'done' : n > 0 || p ? 'partial' : 'none';
    let timing: Row['timing'] = '', lateDays = 0;
    if (due) {
      if (done) { lateDays = Math.max(0, dayDiff(localDay(p!.completed_at!), due)); timing = lateDays > 0 ? 'late' : 'on time'; }
      else timing = due < t ? 'missing' : 'not due yet';
    }
    const scores = passes.filter(x => x.total).map(x => (x.correct ?? 0) / x.total!);
    return {
      id: s.user_id, name: s.display_name, username: s.username, p, passes, due, extended: due !== a.due_date, status, timing, lateDays,
      best: scores.length ? Math.round(Math.max(...scores) * 100) : null,
      avg: scores.length ? Math.round((scores.reduce((x, y) => x + y, 0) / scores.length) * 100) : null,
      words: words[s.user_id] ?? 0,
      pct: Math.min(100, Math.round((n / Math.max(1, a.required_count)) * 100)),
    };
  }), [students, a, progressFor, passesFor, dueFor, words, t]);

  const order = { none: 0, partial: 1, done: 2 };
  const sorted = [...rows].sort((x, y) => (sort === 'name' ? x.name.localeCompare(y.name) : order[x.status] - order[y.status] || x.name.localeCompare(y.name)));
  const doneN = rows.filter(r => r.status === 'done').length;
  const lateN = rows.filter(r => r.timing === 'late').length;
  const missingN = rows.filter(r => r.timing === 'missing').length;
  const bests = rows.map(r => r.best).filter((x): x is number => x != null);

  const saveExt = async (sid: string, d: string | null) => {
    setBusy(true); setError('');
    try { await setExtension(a.id, sid, d); await reloadExtensions(); setEditing(null); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not change the due date.'); }
    setBusy(false);
  };

  const csv = () => {
    const pw = passWord(a.kind, 2);
    const head = ['Student', 'Username', 'Status', `${pw[0].toUpperCase()}${pw.slice(1)} done`, 'Required', 'Completion %', 'Finished', 'Due date', 'On time', 'Days late',
      ...(a.kind === 'grammar' ? ['Best score %', 'Average score %'] : []), 'Time spent', ...(a.kind === 'story' ? ['Lookups'] : []), ...(a.kind === 'video' ? ['Video played'] : []), 'Words saved', 'Visits', 'Last active'];
    const lines = sorted.map(r => [
      r.name, r.username, r.status === 'done' ? 'Done' : r.status === 'partial' ? 'Started' : 'Not started',
      r.p?.completions ?? 0, a.required_count, r.pct, r.p?.completed_at ? localDay(r.p.completed_at) : '', r.due ?? '',
      r.timing, r.lateDays || '',
      ...(a.kind === 'grammar' ? [r.best ?? '', r.avg ?? ''] : []),
      minutes(r.p?.seconds_spent ?? 0), ...(a.kind === 'story' ? [r.p?.lookups ?? 0] : []), ...(a.kind === 'video' ? [minutes(r.p?.video_watched ?? 0)] : []),
      r.words, r.p?.visits ?? 0, r.p ? localDay(r.p.last_active_at) : '',
    ]);
    const text = [head, ...lines].map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([`﻿${text}`], { type: 'text/csv' }));
    const link = document.createElement('a'); link.href = url; link.download = `${a.title.replace(/[^\w\- ]+/g, '').trim() || 'assignment'} results.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  if (students.length === 0) return null;

  return (
    <div className="panel results">
      <div className="panel-head">
        <h3>📊 Results</h3>
        <div className="row" style={{ gap: 6 }}>
          <select value={sort} onChange={e => setSort(e.target.value as 'name' | 'status')} style={{ width: 'auto' }} aria-label="Sort">
            <option value="name">By name</option>
            <option value="status">Not done first</option>
          </select>
          <button type="button" className="small" onClick={() => void refresh()}>↻ Refresh</button>
          <button type="button" className="small" onClick={csv}>⬇ Download CSV</button>
        </div>
      </div>
      <div className="results-summary">
        <span><strong>{doneN}</strong>/{rows.length} done</span>
        {a.due_date && <span><strong>{lateN}</strong> late</span>}
        {a.due_date && <span className={missingN ? 'bad-text' : ''}><strong>{missingN}</strong> missing</span>}
        {bests.length > 0 && <span>average best score <strong>{Math.round(bests.reduce((x, y) => x + y, 0) / bests.length)}%</strong></span>}
        <span className="muted">{a.due_date ? `Due ${shortDate(a.due_date)}` : 'No due date'} · needs {a.required_count} {passWord(a.kind, a.required_count)}</span>
      </div>
      {error && <div className="notice bad">{error}</div>}
      <div className="table-wrap">
        <table className="grid results-table">
          <thead>
            <tr>
              <th>Student</th><th>Status</th><th>{a.kind === 'grammar' ? 'Lessons' : a.kind === 'video' ? 'Watches' : 'Reads'}</th>
              <th>{a.kind === 'grammar' ? 'Scores' : a.kind === 'video' ? 'Watched' : 'Reading'}</th>
              <th>Words saved</th><th>Visits</th><th>Due date</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.id} className={`res-${r.status}`}>
                <td><div className="term">{r.name}</div><div className="small muted">{r.username}</div></td>
                <td>
                  <div className={`res-status ${r.status}`}>{r.status === 'done' ? '✓ Done' : r.status === 'partial' ? `${r.pct}% done` : 'Not started'}</div>
                  {r.timing && <div className={`small res-timing ${r.timing.replace(' ', '-')}`}>{r.timing === 'late' ? `Late (${r.lateDays} day${r.lateDays === 1 ? '' : 's'})` : r.timing === 'on time' ? 'On time' : r.timing === 'missing' ? 'Missing' : 'Not due yet'}</div>}
                  {r.p?.completed_at && <div className="small muted">Finished {shortDate(r.p.completed_at)}</div>}
                </td>
                <td>
                  <div>{r.p?.completions ?? 0}/{a.required_count}</div>
                  {r.passes.length > 0 && (
                    <div className="passes">
                      {r.passes.map(x => (
                        <span key={x.n} className="pass-chip" title={`${shortDate(x.finished_at)}${a.kind === 'story' ? `, ${x.lookups} lookups` : ''}`}>
                          {shortDate(x.finished_at).replace(/^\w+, /, '')}: {x.total ? `${Math.round(((x.correct ?? 0) / x.total) * 100)}%` : clock(x.seconds)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>
                  {a.kind === 'grammar'
                    ? (r.best != null ? <><div>Best <strong>{r.best}%</strong></div><div className="small muted">Average {r.avg}%</div></> : <span className="muted">—</span>)
                    : a.kind === 'video' ? <div>{minutes(r.p?.video_watched ?? 0)}</div>
                      : <><div>{minutes(r.p?.seconds_spent ?? 0)}</div><div className="small muted">{r.p?.lookups ?? 0} lookup{r.p?.lookups === 1 ? '' : 's'}</div></>}
                  {a.kind === 'grammar' && <div className="small muted">{minutes(r.p?.seconds_spent ?? 0)} practising</div>}
                </td>
                <td>{r.words}</td>
                <td><div>{r.p?.visits ?? 0}</div>{r.p && <div className="small muted">last {shortDate(r.p.last_active_at)}</div>}</td>
                <td className="res-due">
                  {editing === r.id ? (
                    <div className="row" style={{ gap: 4 }}>
                      <input type="date" value={date} min={a.due_date ?? undefined} onChange={e => setDate(e.target.value)} style={{ width: 'auto' }} />
                      <button type="button" className="small primary" disabled={busy || !date} onClick={() => void saveExt(r.id, date)}>Save</button>
                      <button type="button" className="small" onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div>{r.due ? shortDate(r.due) : <span className="muted">none</span>}{r.extended && <span className="ext-badge">extended</span>}</div>
                      <div className="row" style={{ gap: 4 }}>
                        <button type="button" className="linkish small" onClick={() => { setEditing(r.id); setDate(r.due && r.due >= t ? r.due : t); }}>{r.extended ? 'Change' : 'Extend'}</button>
                        {r.extended && <button type="button" className="linkish small" disabled={busy} onClick={() => void saveExt(r.id, null)}>Remove</button>}
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="small muted">An extension gives that student their own due date: the assignment stays open for them until then, and “on time” is judged against it.</p>
    </div>
  );
}
